/**
 * Everything that has happened to one party, newest first.
 *
 * Shared, because a lead and a client are the same record at two ages and the
 * `Activity` rows follow them across: converting a juhuklient moves its
 * activities onto the client it becomes. Two lists that render the same rows
 * differently would mean the history changes appearance at the moment the
 * relationship becomes real - and they did, in the smallest way that still
 * matters: both printed the stored `type` in a badge, so a message left on the
 * phone read as "voicemail" on either screen.
 *
 * The list used to be a formatting detail of the two pages that hold it, and
 * that was fine while the only writer was the public form bridge. The voicemail
 * intake now files an entry for every message left on the phone, which makes
 * this the answer to "why is this company in our pipeline at all" - and an entry
 * that came from a call has somewhere to go, which a badge cannot express.
 *
 * The icon is what makes it scannable: three entries deep, the shape in the left
 * column separates a voicemail from a form reply faster than either label does.
 * An unrecognised type keeps the neutral note icon and shows its raw value, so
 * an imported row degrades to what it looked like before instead of vanishing.
 *
 * ── Planned steps ────────────────────────────────────────────────────────────
 *
 * An entry with a `dueAt` is not a record of something that happened - it is
 * something somebody still has to do, and it lives in the same list because the
 * plan and the history are one story. It wears its date, it turns red once that
 * date passes, and when `onDone` is given it carries the control that completes
 * it. Without `onDone` the list is exactly what it was: the read-only places
 * that draw a timeline keep drawing one.
 */
import { Link } from "react-router-dom";
import {
  Activity as ActivityIcon,
  ArrowRightLeft,
  ArrowUpRight,
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  Mail,
  Phone,
  Receipt,
  StickyNote,
  Voicemail,
  type LucideIcon,
} from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { activityTypeLabel } from "../../../lib/activityType";
import { daysUntil } from "../../../lib/nextStep";
import type { Activity } from "../../../lib/clients";
import { fmtDateTime } from "../../ui";

const ICONS: Record<string, LucideIcon> = {
  note: StickyNote,
  call: Phone,
  voicemail: Voicemail,
  form: ClipboardList,
  email: Mail,
  status_change: ArrowRightLeft,
  meeting: CalendarDays,
  // The project timeline's billing entry. `Receipt` is the same glyph the
  // invoices tab draws one row up on the project page, so the money moment looks
  // like money wherever the operator meets it.
  invoiced: Receipt,
  // A confirmed outage of a watched site, filed when it resolved. The same
  // pulse glyph as the Saidiseire nav row, so the entry reads as coming from
  // there.
  site_monitor: ActivityIcon,
};

export function ActivityList({
  rows,
  empty,
  onDone,
}: {
  rows: Activity[];
  empty: string;
  /** Completes a planned step. Absent on the read-only timelines, and the
   *  control is absent with it rather than rendered disabled. */
  onDone?: (activity: Activity) => void;
}) {
  const t = useT();

  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;

  return (
    <ul className="space-y-3">
      {rows.map((a) => {
        const Icon = ICONS[a.type] ?? StickyNote;
        // Outstanding is both at once: promised, and not yet carried out. A
        // completed step keeps its date and stops being owed - see the schema
        // comment on `Activity.doneAt`.
        const outstanding = Boolean(a.dueAt) && !a.doneAt;
        const late = outstanding && daysUntil(a.dueAt!) < 0;
        return (
          <li key={a.id} className="flex gap-3 text-sm">
            <span className="mt-0.5 flex size-7 flex-none items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Icon className="size-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {activityTypeLabel(a.type, t)}
                {" · "}
                <span className="crm-tnum">{fmtDateTime(a.createdAt)}</span>
                {/* The actor keeps its own casing. It is a name, typed or
                    composed by whatever filed the entry ("Vorm: Kontakt"), and
                    shouting it is both wrong and harder to read than the label
                    it follows. */}
                {a.actor ? <span className="normal-case"> · {a.actor}</span> : null}
              </p>
              {a.body && <p className="whitespace-pre-wrap">{a.body}</p>}
              {/* The plan line. Only on an entry that is still owed - a
                  completed one has already said everything it has to say, and a
                  "done" stamp on every old note would bury the two that are
                  not. */}
              {outstanding ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span
                    className={
                      late
                        ? "inline-flex items-center gap-1 text-xs font-semibold text-destructive"
                        : "inline-flex items-center gap-1 text-xs font-semibold text-amber-600 dark:text-amber-400"
                    }
                  >
                    <CalendarClock className="size-3" />
                    {late
                      ? t("nextStep.lateBy", { days: Math.abs(daysUntil(a.dueAt!)) })
                      : t("nextStep.dueOn", { when: fmtDateTime(a.dueAt!) })}
                  </span>
                  {onDone ? (
                    <button type="button" className="crm-cb" onClick={() => onDone(a)} title={t("nextStep.markDone")}>
                      <CalendarCheck2 className="size-3.5" />
                      {t("nextStep.markDone")}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {/* The recording and the transcript, one click away. Only the
                  phone writes this column, so the link never shows up on an
                  entry with nothing to play. */}
              {a.callLogId && (
                <Link
                  to={`/calls/${a.callLogId}`}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-primary"
                >
                  {t("activity.openCall")}
                  <ArrowUpRight className="size-3" />
                </Link>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
